import axios from 'axios';
import {
  BASE_URL,
  VIETNAM_UPLOAD_LIST_API_PATH,
  VIETNAM_UPLOAD_MULTIPART_BASE_API_PATH,
} from 'constants/api';
import { UPLOAD_CHUNK_SIZE } from 'constants/constant';
import dayjs from 'dayjs';
import { BatchJobResponse } from 'src/domain/video/asset-source/type/videoSourceType';
import { FetchResult } from 'type/apiType';
import { SUBTITLE_CONTENT_TYPES } from 'utils/vietnamUploadFileType';

export interface VietnamUploadPart {
  ETag: string;
  PartNumber: number;
}

interface StartResponse {
  key: string;
  upload_id: string;
}

interface CompleteResponse {
  key: string;
  location?: string;
}

// presigned-url 응답이 순수 문자열인지 객체인지 백엔드마다 다를 수 있어 모두 수용한다.
type PresignedUrlResponse = string | { url?: string; presigned_url?: string };

export interface VietnamUploadHandlers {
  // percent: 0~100, speed: bytes/sec
  onProgress?: (percent: number, speed: number) => void;
  // 사용자 취소용. abort 시 진행 중인 파트 PUT을 중단하고 서버 abort를 호출한다.
  signal?: AbortSignal;
}

// 서버: {BASE_URL}/api/v1/vietnam-upload/multipart/<action>/ (끝 슬래시 필수)
// put-url: 소용량 단일 PUT용 엔드포인트 (현재는 항상 multipart 사용 — 필요 시 분기 추가)
type UploadAction = 'start' | 'presigned-url' | 'complete' | 'abort' | 'put-url';

const api = async <T>(action: UploadAction, body: Record<string, unknown>): Promise<T> => {
  const url = `${BASE_URL}${VIETNAM_UPLOAD_MULTIPART_BASE_API_PATH}/${action}/`;
  const response = await axios.post<T>(url, body);
  return response.data;
};

/**
 * S3 object key 생성 (프론트 책임).
 * 규칙: `vietnam/{companySlug}/{yyyymmdd}/{filename}`.
 * company 세그먼트로 CP 간 격리 — 단, 격리 강제는 서버 책임이다:
 * 백엔드는 key의 company 세그먼트를 `request.user.company`로 검증(불일치 거부/치환)해야 한다.
 * 서버는 key 검증(`..`/`://`/빈값 거부, 앞뒤 `/` 정규화)도 수행한다.
 * 주의: 동일 key로 complete하면 덮어쓰기 (같은 날 같은 파일명이면 덮어써짐).
 */
export const buildVietnamUploadKey = (companySlug: string, fileName: string): string => {
  const date = dayjs().format('YYYYMMDD');
  // 경로 구분자/앞뒤 공백만 최소 정리 — 나머지 검증은 서버가 수행한다.
  const safeCompany = companySlug
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/\\/g, '/');
  const safeName = fileName.trim().replace(/^\/+/, '').replace(/\\/g, '/');
  return `vietnam/${safeCompany}/${date}/${safeName}`;
};

// 확장자 → 비디오 MIME 폴백. 브라우저가 file.type를 비워 주는 경우(일부 .mkv/.mov 등)에도
// 정확한 content_type을 백엔드로 넘겨, 서빙 시 올바른 Content-Type으로 저장되게 한다.
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  webm: 'video/webm',
  ts: 'video/mp2t',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  '3gp': 'video/3gpp',
};

const resolveContentType = (file: File): string => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  // 자막은 확장자 매핑을 **브라우저 값보다 우선**한다. 브라우저가 `.srt`에 빈 문자열이나
  // `text/plain`을 주는 등 제각각이라, 그대로 두면 같은 형식이 레코드마다 다른 타입으로 남는다.
  if (SUBTITLE_CONTENT_TYPES[ext]) {
    return SUBTITLE_CONTENT_TYPES[ext];
  }
  if (file.type) {
    return file.type;
  }
  return EXTENSION_CONTENT_TYPES[ext] ?? 'application/octet-stream';
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const isAbortError = (error: unknown): boolean => {
  const err = error as { name?: string; code?: string };
  return (
    err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED'
  );
};

const resolvePresignedUrl = (response: PresignedUrlResponse): string => {
  if (typeof response === 'string') {
    return response;
  }
  const url = response?.url ?? response?.presigned_url;
  if (!url) {
    throw new Error('presigned-url 응답에서 URL을 찾을 수 없습니다.');
  }
  return url;
};

/**
 * 단일 파트 PUT. presigned URL은 만료(600초) 위험 때문에 PUT 직전에 발급받고,
 * 실패(403/만료/네트워크/5xx) 시 같은 PartNumber로 presigned를 재발급하여 재시도한다.
 */
const putPartWithRetry = async (
  key: string,
  uploadId: string,
  partNumber: number,
  chunk: Blob,
  onPartProgress: (loadedBytes: number) => void,
  signal?: AbortSignal,
  maxRetries = 3
): Promise<string> => {
  let attempt = 0;
  let lastError: unknown = null;

  while (attempt < maxRetries) {
    try {
      const presignedResponse = await api<PresignedUrlResponse>('presigned-url', {
        key,
        upload_id: uploadId,
        part_number: partNumber,
      });
      const presignedUrl = resolvePresignedUrl(presignedResponse);

      const putResponse = await axios.put(presignedUrl, chunk, {
        headers: { 'Content-Type': chunk.type || 'application/octet-stream' },
        signal,
        onUploadProgress: progressEvent => onPartProgress(progressEvent.loaded),
      });

      // S3는 ETag를 헤더로 반환 (프록시 CORS ExposeHeaders에 ETag 필요).
      const etag = putResponse.headers?.etag ?? putResponse.headers?.ETag;
      if (!etag) {
        throw new Error(`part ${partNumber}: ETag 없음 (프록시 CORS ExposeHeaders 확인)`);
      }
      return etag;
    } catch (error) {
      // 사용자 취소는 재시도하지 않는다.
      if (isAbortError(error) || signal?.aborted) {
        throw error;
      }
      lastError = error;
      attempt++;
      if (attempt >= maxRetries) {
        break;
      }
      // 지수 백오프 (최대 5초). 다음 시도에서 presigned를 재발급하므로 만료도 함께 해소된다.
      await delay(Math.min(1000 * Math.pow(2, attempt - 1), 5000));
    }
  }

  throw lastError;
};

// 소용량 파일은 multipart 대신 단일 PUT(`put-url`)을 사용한다. 임계값 = 파트 크기 1개.
const SINGLE_PUT_MAX_SIZE = UPLOAD_CHUNK_SIZE;

// 누적 업로드 바이트 → (percent, speed) 방출기. single/multipart 공통.
const createProgressEmitter = (
  totalSize: number,
  onProgress?: (percent: number, speed: number) => void
): ((uploadedBytes: number) => void) => {
  let lastBytes = 0;
  let lastTime = performance.now();
  return (uploadedBytes: number): void => {
    if (!onProgress) {
      return;
    }
    const uploaded = Math.min(uploadedBytes, totalSize);
    const percent = totalSize > 0 ? (uploaded / totalSize) * 100 : 100;
    const now = performance.now();
    const elapsedSeconds = (now - lastTime) / 1000;
    const speed = elapsedSeconds > 0 ? (uploaded - lastBytes) / elapsedSeconds : 0;
    lastBytes = uploaded;
    lastTime = now;
    onProgress(Math.min(100, percent), Math.max(0, speed));
  };
};

/**
 * 소용량 단일 PUT: `put-url`로 presigned URL을 받아 파일 전체를 한 번에 PUT한다.
 * multipart lifecycle(start/complete/abort)이 없으므로 실패 시 별도 정리가 필요 없다.
 * 실패(403/만료/네트워크/5xx) 시 put-url 재발급 후 재시도한다.
 */
const uploadSingle = async (
  file: File,
  key: string,
  handlers: VietnamUploadHandlers
): Promise<CompleteResponse> => {
  const { onProgress, signal } = handlers;
  const contentType = resolveContentType(file);
  const emitProgress = createProgressEmitter(file.size, onProgress);

  let attempt = 0;
  let lastError: unknown = null;

  while (attempt < 3) {
    try {
      if (signal?.aborted) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }
      const presigned = await api<PresignedUrlResponse>('put-url', {
        key,
        content_type: contentType,
        file_size: file.size,
      });
      const putUrl = resolvePresignedUrl(presigned);

      await axios.put(putUrl, file, {
        headers: { 'Content-Type': contentType },
        signal,
        onUploadProgress: progressEvent => emitProgress(progressEvent.loaded),
      });
      return { key };
    } catch (error) {
      if (isAbortError(error) || signal?.aborted) {
        throw error;
      }
      lastError = error;
      attempt++;
      if (attempt >= 3) {
        break;
      }
      await delay(Math.min(1000 * Math.pow(2, attempt - 1), 5000));
    }
  }

  throw lastError;
};

/**
 * 대용량 multipart 업로드: start → 파트별 presigned+PUT → complete.
 * 전체 실패 또는 사용자 취소 시 abort를 호출해 미완료 파트를 정리한다.
 */
const uploadMultipart = async (
  file: File,
  key: string,
  handlers: VietnamUploadHandlers
): Promise<CompleteResponse> => {
  const { onProgress, signal } = handlers;

  const started = await api<StartResponse>('start', {
    key,
    content_type: resolveContentType(file),
    file_size: file.size,
  });
  const uploadId = started.upload_id;
  // 서버가 정규화한 key를 이후 요청에 사용한다.
  const normalizedKey = started.key || key;

  const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));
  const parts: VietnamUploadPart[] = [];
  const emitProgress = createProgressEmitter(file.size, onProgress);
  let completedBytes = 0;

  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (signal?.aborted) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }
      const start = (partNumber - 1) * UPLOAD_CHUNK_SIZE;
      const end = Math.min(partNumber * UPLOAD_CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const etag = await putPartWithRetry(
        normalizedKey,
        uploadId,
        partNumber,
        chunk,
        loaded => emitProgress(completedBytes + loaded),
        signal
      );

      parts.push({ ETag: etag, PartNumber: partNumber });
      completedBytes += chunk.size;
      emitProgress(completedBytes);
    }

    return await api<CompleteResponse>('complete', {
      key: normalizedKey,
      upload_id: uploadId,
      parts,
      // 업로드 레코드에 크기/타입을 채우도록 complete에 함께 전달 (계약상 선택 필드).
      content_type: resolveContentType(file),
      file_size: file.size,
    });
  } catch (error) {
    // 전체 실패/취소 시 미완료 파트 정리. abort 실패는 무시한다.
    await api('abort', { key: normalizedKey, upload_id: uploadId }).catch(() => undefined);
    throw error;
  }
};

/**
 * Vietnam 전용 파일 업로드: Video/content 레코드 없이 S3로 파일만 올린다.
 * 소용량(≤ 파트 1개)은 단일 PUT, 그 이상은 multipart로 분기한다.
 */
export const uploadFileToVietnam = async (
  file: File,
  key: string,
  handlers: VietnamUploadHandlers = {}
): Promise<CompleteResponse> => {
  if (file.size <= SINGLE_PUT_MAX_SIZE) {
    return uploadSingle(file, key, handlers);
  }
  return uploadMultipart(file, key, handlers);
};

export { isAbortError as isUploadCancelled };

// ---------------------------------------------------------------------------
// 내 업로드 목록 + Ingest (회사 스코프는 서버가 request.user.company로 강제)
// ---------------------------------------------------------------------------

export type VietnamUploadStatus = 'uploaded' | 'ingesting' | 'ingested' | 'failed';

export interface VietnamUploadItem {
  id: number;
  key: string;
  file_name: string;
  file_size: number | null;
  content_type: string | null;
  company_slug: string;
  uploaded_by: string | null;
  created: string;
  status: VietnamUploadStatus;
  video_id: number | null;
  batch_job_id: string | null;
}

/**
 * 서버가 받는 파라미터 전부. **데이터 스코프 파라미터는 없다** —
 * 자기 company만 볼지 전 CP를 볼지는 서버가 호출자 권한으로 결정하므로
 * 클라이언트가 요청할 수단이 없고, 있어서도 안 된다.
 *
 * company slug로 좁히는 서버측 필터도 아직 없다. 화면의 Company 필터는 현재 페이지
 * 범위에서만 동작한다 (`vietnamUploadsList.tsx` 참고).
 */
export interface VietnamUploadListParams {
  status?: VietnamUploadStatus;
  search?: string;
  page?: number;
  page_size?: number;
}

const buildQuery = (params?: VietnamUploadListParams): string => {
  if (!params) {
    return '';
  }
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.page_size) query.set('page_size', String(params.page_size));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

// GET /api/v1/vietnam-upload/uploads/ — 서버가 호출자 company로 스코프한 내 업로드만 반환.
export const listVietnamUploads = async (
  params?: VietnamUploadListParams
): Promise<FetchResult<VietnamUploadItem>> => {
  const url = `${BASE_URL}${VIETNAM_UPLOAD_LIST_API_PATH}${buildQuery(params)}`;
  const response = await axios.get<FetchResult<VietnamUploadItem>>(url);
  return response.data;
};

export interface VietnamUploadDownload {
  url: string;
  file_name: string;
  expires_in: number;
}

/**
 * GET /api/v1/vietnam-upload/uploads/{id}/download-url/ — 원본 다운로드용 presigned GET.
 *
 * 자막을 위해 생긴 경로다. 자막은 인제스트 대상이 아니라(Subtitle 은 Video 가 아니라 Content 에
 * 붙는다) 운영진이 내려받아 기존 Content 자막 화면으로 붙인다.
 *
 * URL 은 만료(600초)가 있으므로 **받아서 바로 쓰고 저장하지 않는다.**
 */
export const getVietnamUploadDownloadUrl = async (
  uploadId: number
): Promise<VietnamUploadDownload> => {
  const url = `${BASE_URL}${VIETNAM_UPLOAD_LIST_API_PATH}${uploadId}/download-url/`;
  const response = await axios.get<VietnamUploadDownload>(url);
  return response.data;
};

// POST /api/v1/vietnam-upload/uploads/{id}/ingest/ — 백엔드가 copy-asset-source를 감싸 실행하고
// 업로드 레코드 상태(ingesting→ingested/failed)와 video_id/batch_job_id를 갱신한다.
export const ingestVietnamUpload = async (uploadId: number): Promise<BatchJobResponse> => {
  const url = `${BASE_URL}${VIETNAM_UPLOAD_LIST_API_PATH}${uploadId}/ingest/`;
  const response = await axios.post<BatchJobResponse>(url, {});
  return response.data;
};
